import { DecimalPipe, TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { AuthorizeService } from 'src/app/_core/access-control/authorize.service';
import { MapConfig } from 'src/app/_shared/config/map.config';
import { PartialProperty } from 'src/app/_shared/interface/property.interface';
import { DurationPipe } from 'src/app/_shared/pipe/duration.pipe';
import { ToStringPipe } from 'src/app/_shared/pipe/to-string.pipe';
import { FavoriteService } from 'src/app/_shared/service/favorite.service';
import { FiltersState } from 'src/app/_shared/state/filter.state';
import { MapBounds, MapCenter } from 'src/app/shared/interface/map.interface';
import { SubscriptionPlanService } from 'src/app/shared/service/subscription-plan.service';
import MESSAGE from 'src/assets/resource/strings.json';
import constants from 'src/constant.json';
import { environment } from 'src/environments/environment';
import { NumberFormatterPipe } from '../../_shared/pipe/number.pipe';
import { WorkspaceState } from '../../_shared/state/workspace.state';
import { MapState } from '../state/map.state';
import { MarkerState } from '../state/marker.state';
import { PropertyState } from '../state/property.state';
import { WorkspaceService } from './workspace.service';

@Injectable({
  providedIn: 'root',
})
export class MapService {
  activeDistressInfo: any = {};
  activeListingInfo: any = {};
  stringRes: any = MESSAGE;
  constructor(
    private authorizeService: AuthorizeService,
    private decimalPipe: DecimalPipe,
    private durationPipe: DurationPipe,
    private favoriteService: FavoriteService,
    private filterState: FiltersState,
    private mapConfig: MapConfig,
    private mapState: MapState,
    private markerState: MarkerState,
    private numberPipe: NumberFormatterPipe,
    private propertyState: PropertyState,
    private titleCasePipe: TitleCasePipe,
    private toStringPipe: ToStringPipe,
    private workspaceService: WorkspaceService,
    private workspaceState: WorkspaceState,
    private subscriptionService: SubscriptionPlanService,
  ) {
    this.initSubscription();
  }

  setCenter(center: MapCenter) {
    this.mapState.mapCenterValue = center;
  }

  setBoundingBox(bounds: MapBounds) {
    this.mapState.mapBoundsValue = bounds;
  }

  setZoom(zoom: number) {
    this.mapState.mapZoomValue = zoom;
  }

  initSubscription() {
    this.propertyState.listingInformation$.subscribe((listingInfo: any) => {
      //console.log('$#%@ map-service-5'); // do not remove
      this.activeListingInfo = listingInfo;
      // this.replaceListingInfoWindowContent();
    });

    this.propertyState.activePropertyDetails$.subscribe((response: any) => {
      //console.log('$#%@ map-service-3'); // do not remove
      this.replaceBasicInfoWindowContent(response);
      if (response.distressedDetails && response.distressedDetails.foreclosure) {
        this.activeDistressInfo = response.distressedDetails.foreclosure;
      }
    });
  }

  openInfoWindow(marker) {
    if (marker.meta.propertyList.length > 0) {
      marker.meta.propertyList = Array.from(
        marker.meta.propertyList
          .reduce((map, obj) => map.set(obj.Address, { ...map.get(obj.Address), ...obj }), new Map())
          .values()
      );
    }
    this.closeActiveInfoWindow();
    let pmxPropertyId = marker.meta.propertyList[0].PMXPropertyId;
    this.mapState.activeInfoWindowValue = new google.maps.InfoWindow({
      content: this.getInfoWindowContent(marker.meta.propertyList),
      disableAutoPan: true,
    });

    this.setInfoWindowOffset(marker);
    this.mapState.activeInfoWindowValue.open(this.mapState.workspaceMap, marker);
    google.maps.event.addListenerOnce(this.mapState.activeInfoWindowValue, 'domready', () => {
      if (document.querySelector(`#fav${pmxPropertyId}`)) {
        document.querySelector(`#fav${pmxPropertyId}`).addEventListener('click', (event) => {
          event.stopPropagation();
          if (this.authorizeService.canAct('favoriteAdd')) {
            this.followProperty(marker?.meta?.propertyList[0]);
          }
        });
      }
      if (document.querySelector(`.mapcard_base#pmx${pmxPropertyId}`)) {
        document.querySelector(`.mapcard_base#pmx${pmxPropertyId}`).addEventListener('click', () => {
          if (this.authorizeService.canAct('propertyDetails')) {
            this.workspaceService.openPropertyDetailsSheet(marker.meta.propertyList[0]);
          }
        });
      }
      if (document.querySelector(`#detailMultiple${pmxPropertyId}`)) {
        document.querySelector(`#detailMultiple${pmxPropertyId}`).addEventListener('click', () => {
          if (this.authorizeService.canAct('propertyDetails')) {
            this.propertyState.activeMultiplePropertyDetailsValue = marker.meta.propertyList;
            let propertyParams: any = {
              PMXPropertyId: pmxPropertyId,
              logInput: true,
            };
            this.propertyState.propertyDetailsSheetInput = propertyParams;
            this.workspaceState.multiplePropertyDetailsSheetLoaderValue = true;
          }
        });
      }
    });
  }

  closeActiveInfoWindow() {
    if (this.mapState.activeInfoWindowValue) this.mapState.activeInfoWindowValue.close();
  }

  setInfoWindowOffset(marker) {
    const center = this.mapState.workspaceMap.getProjection().fromLatLngToPoint(this.mapState.workspaceMap.getCenter());
    const point = this.mapState.workspaceMap.getProjection().fromLatLngToPoint(marker.getPosition());
    let quadrant = '';
    quadrant += point.y > center.y ? 'b' : 't';
    quadrant += point.x < center.x ? 'l' : 'r';
    document.getElementById('map').classList.remove('trWindow');
    document.getElementById('map').classList.remove('tlWindow');
    document.getElementById('map').classList.remove('brWindow');
    document.getElementById('map').classList.remove('blWindow');
    if (quadrant === 'tr') {
      document.getElementById('map').classList.add('trWindow');
    } else if (quadrant === 'tl') {
      document.getElementById('map').classList.add('tlWindow');
    } else if (quadrant === 'br') {
      document.getElementById('map').classList.add('brWindow');
    } else if (quadrant === 'bl') {
      document.getElementById('map').classList.add('blWindow');
    }
  }

  followProperty(property) {
    if (!this.subscriptionService.checkFeatureValidity(constants.PLAN_BASED_FEATURES.favorite.key)) return;

    if (property.isFavorite && property.PMXPropertyId) {
      const inputParams = {
        PMXPropertyId: property.PMXPropertyId,
      };
      this.updateFavoriteStatusForMarker(property.PMXPropertyId, false);
      this.favoriteService.unFollowFavorite(inputParams).subscribe(
        ({ status }) => {
          if (!status || status != 'success') this.updateFavoriteStatusForMarker(property.PMXPropertyId, true);
        },
        (error) => {
          this.updateFavoriteStatusForMarker(property.PMXPropertyId, true);
        }
      );
    } else if (property.StateOrProvince && property.PMXPropertyId) {
      const inputParams = {
        PMXPropertyIdList: [property.PMXPropertyId.toString()],
        GroupIds: [this.favoriteService.getDefaultGroup()],
        stateList: [property.StateOrProvince],
      };
      this.updateFavoriteStatusForMarker(property.PMXPropertyId, true);
      this.favoriteService.addFavorites(inputParams).subscribe(
        ({ status }) => {
          if (!status || status != 'success') this.updateFavoriteStatusForMarker(property.PMXPropertyId, false);
        },
        (error) => {
          this.updateFavoriteStatusForMarker(property.PMXPropertyId, false);
        }
      );
    }
  }

  updateFavoriteStatusForMarker(pmxPropertyId, isFavorite) {
    this.markerState.renderMarkerValue = false;
    if (this.markerState.mapMarkerListValue) {
      Object.keys(this.markerState.mapMarkerListValue).some((key) => {
        this.markerState.mapMarkerListValue[key].meta.propertyList.some((res, index) => {
          if (res.PMXPropertyId == pmxPropertyId) {
            this.markerState.mapMarkerListValue[key].meta.propertyList[index].isFavorite = isFavorite;
            return true;
          }
        });
      });
      if (isFavorite) {
        if (document.querySelector(`#fav${pmxPropertyId}`)) {
          document.querySelector(`#fav${pmxPropertyId} .watch-icon`).setAttribute('fill', '#ec5757');
        }
      } else {
        if (document.querySelector(`#fav${pmxPropertyId}`)) {
          document.querySelector(`#fav${pmxPropertyId} .watch-icon`).setAttribute('fill', '#D1D1D1');
        }
      }
      // this.appRef.tick();
    }
  }

  replaceBasicInfoWindowContent(activeProperty) {
    let infoWindowContent = this.mapConfig.detailedInfoBox;
    const PMXPropertyId = activeProperty.PMXPropertyId;
    if (
      PMXPropertyId &&
      document.querySelector(`#basepmx${PMXPropertyId}`) &&
      activeProperty &&
      Object.keys(activeProperty).length
    ) {
      document.querySelector(`#basepmx${PMXPropertyId} #bed${PMXPropertyId}`).innerHTML =
        activeProperty.BedroomsTotal || 0;
      document.querySelector(`#basepmx${PMXPropertyId} #bath${PMXPropertyId}`).innerHTML =
        activeProperty.BathroomsTotalInteger || 0;
      document.querySelector(`#basepmx${PMXPropertyId} #living${PMXPropertyId}`).innerHTML =
        this.decimalPipe.transform(activeProperty.LivingArea) || '0';
      document.querySelector(`#basepmx${PMXPropertyId} #lot${PMXPropertyId}`).innerHTML =
        this.numberPipe.transform(activeProperty.LotSizeSquareFeet) || '0';
      document.querySelector(`#basepmx${PMXPropertyId} #address${PMXPropertyId}`).innerHTML = `${activeProperty.StreetAddress
        } ${activeProperty.City || ''} ${activeProperty.StateOrProvince || ''}, ${activeProperty.PostalCode || ''}`;
      let imageUrl = activeProperty.Lst_ListingId
        ? `${environment.listingImageBaseUrl}` +
        '?ListingId=' +
        `${activeProperty.Lst_ListingId}` +
        '&imageName=1&height=219'
        : `${environment.listingImageBaseUrl}` + '?ListingId=0&imageName=1&height=219';
      (document.querySelector(`#basepmx${PMXPropertyId} #pmx${PMXPropertyId}`) as HTMLElement).style.backgroundImage =
        'url(' + imageUrl + ')';
    }
  }

  replaceListingInfoWindowContent() {
    let listingInformation = this.activeListingInfo;
    let distressInfo = this.activeDistressInfo;
    const PMXPropertyId = listingInformation.PMXPropertyId;
    const filterList: any = this.filterState.basicFilterListValue;
    if (PMXPropertyId && document.querySelector(`#pmx${PMXPropertyId}`)) {
      if (listingInformation && Object.keys(listingInformation).length) {
        const listingStatus = listingInformation.Lst_StandardStatus;
        if (
          (listingStatus && listingStatus == 'Active') ||
          listingStatus == 'Pending' ||
          listingStatus == 'Active Under Contract'
        ) {
          document.querySelector(`#activeListingInfo${PMXPropertyId}`).classList.remove('hide');
          document.querySelector(`#activeListingInfo${PMXPropertyId} #listStatus`).innerHTML =
            listingInformation.Lst_StandardStatus ? listingInformation.Lst_StandardStatus : '--';
          document.querySelector(`#activeListingInfo${PMXPropertyId} #listPrice`).innerHTML =
            listingInformation.Lst_ListPrice
              ? `<sup>$</sup>${this.numberPipe.transform(listingInformation.Lst_ListPrice)}`
              : '--';
          document.querySelector(`#activeListingInfo${PMXPropertyId} #listSold`).innerHTML =
            listingInformation.Lst_ClosePrice
              ? `<sup>$</sup>${this.numberPipe.transform(listingInformation.Lst_ClosePrice)}`
              : '--';
          document.querySelector(`#activeListingInfo${PMXPropertyId} #listMarket`).innerHTML =
            listingInformation.Lst_ListingContractDate
              ? this.durationPipe.transform(listingInformation.Lst_ListingContractDate)
              : '--';
          if (filterList.FlipDuration) {
            document.querySelector(`#activeListingInfo${PMXPropertyId} #flipDuration`).innerHTML =
              listingInformation.Lst_FlipDuration ? `${listingInformation.Lst_FlipDuration} days` : '--';
          } else {
            document.querySelector(`#activeListingWithFlipInfo${PMXPropertyId}`)
              ? document.querySelector(`#activeListingWithFlipInfo${PMXPropertyId}`).classList.add('hide')
              : null;
          }
          if (
            (filterList.DistressStatus || filterList.DistressStartDateWithin) &&
            distressInfo &&
            distressInfo.DistressStatus
          ) {
            document.querySelector(`#activeListingInfo${PMXPropertyId}`).classList.remove('hide');
            document.querySelector(`#activeListingInfo${PMXPropertyId} #distressStatus`).innerHTML =
              distressInfo.DistressStatus ? this.titleCasePipe.transform(distressInfo.DistressStatus) : '--';
            document.querySelector(`#onlyDistressInfo${PMXPropertyId}`)
              ? document.querySelector(`#onlyDistressInfo${PMXPropertyId}`).classList.add('hide')
              : null;
          } else {
            if (document.querySelector(`#activeListingWithDistressInfo${PMXPropertyId}`)) {
              document.querySelector(`#activeListingWithDistressInfo${PMXPropertyId}`).classList.add('hide');
            }
          }
          if (!filterList.FlipDuration && !filterList.DistressStatus && !filterList.DistressStartDateWithin) {
            document.querySelector(`#lineBreak${PMXPropertyId}`)
              ? document.querySelector(`#lineBreak${PMXPropertyId}`).classList.add('hide')
              : null;
          }
          document.querySelector(`#nonActiveListingInfo${PMXPropertyId}`)
            ? document.querySelector(`#nonActiveListingInfo${PMXPropertyId}`).classList.add('hide')
            : null;
        } else if (listingStatus) {
          if (document.querySelector(`#activeListingInfo${PMXPropertyId}`)) {
            document.querySelector(`#activeListingInfo${PMXPropertyId}`).classList.add('hide');
          }
          document.querySelector(`#nonActiveListingInfo${PMXPropertyId}`).classList.remove('hide');

          document.querySelector(`#nonActiveListingInfo${PMXPropertyId} #listStatus`).innerHTML =
            listingInformation.Lst_StandardStatus ? listingInformation.Lst_StandardStatus : '--';
          if (listingStatus === 'Sold') {
            document.querySelector(`#nonActiveListingInfo${PMXPropertyId} #listPrice`).innerHTML = '--';
          } else {
            document.querySelector(`#nonActiveListingInfo${PMXPropertyId} #listPrice`).innerHTML =
              listingInformation.Lst_ListPrice ? listingInformation.Lst_ListPrice : '--';
          }
          document.querySelector(`#nonActiveListingInfo${PMXPropertyId} #offMarket`).innerHTML =
            listingInformation.Lst_LastStatusChangeEventTimestamp
              ? this.durationPipe.transform(listingInformation.Lst_LastStatusChangeEventTimestamp)
              : '--';
          if (filterList.FlipDuration) {
            document.querySelector(`#nonActiveListingInfo${PMXPropertyId} #flipDuration`).innerHTML =
              listingInformation.Lst_FlipDuration ? `${listingInformation.Lst_FlipDuration} days` : '--';
          } else {
            document.querySelector(`#nonActiveListingWithFlipInfo${PMXPropertyId}`)
              ? document.querySelector(`#nonActiveListingWithFlipInfo${PMXPropertyId}`).classList.add('hide')
              : null;
          }
          if (
            (filterList.DistressStatus || filterList.DistressStartDateWithin) &&
            distressInfo &&
            distressInfo.DistressStatus
          ) {
            document.querySelector(`#nonActiveListingInfo${PMXPropertyId}`).classList.remove('hide');
            document.querySelector(`#nonActiveListingInfo${PMXPropertyId} #distressStatus`).innerHTML =
              distressInfo.DistressStatus ? this.titleCasePipe.transform(distressInfo.DistressStatus) : '--';
            document.querySelector(`#onlyDistressInfo${PMXPropertyId}`).classList.add('hide');
          } else {
            if (document.querySelector(`#nonActiveListingWithDistressInfo${PMXPropertyId}`)) {
              document.querySelector(`#nonActiveListingWithDistressInfo${PMXPropertyId}`).classList.add('hide');
            }
            if (document.querySelector(`#onlyDistressInfo${PMXPropertyId}`)) {
              document.querySelector(`#onlyDistressInfo${PMXPropertyId}`).classList.add('hide');
            }
          }
          if (!filterList.FlipDuration && !filterList.DistressStatus && !filterList.DistressStartDateWithin) {
            document.querySelector(`#lineBreak${PMXPropertyId}`)
              ? document.querySelector(`#lineBreak${PMXPropertyId}`).classList.add('hide')
              : null;
          }
        } else {
          this.hideListingInfo(PMXPropertyId, distressInfo);
        }
      } else {
        this.hideListingInfo(PMXPropertyId, distressInfo);
      }
    }
  }

  hideListingInfo(PMXPropertyId, distressInfo?) {
    if (document.querySelector(`#nonActiveListingInfo${PMXPropertyId}`)) {
      document.querySelector(`#nonActiveListingInfo${PMXPropertyId}`).classList.add('hide');
    }
    if (document.querySelector(`#activeListingInfo${PMXPropertyId}`)) {
      document.querySelector(`#activeListingInfo${PMXPropertyId}`).classList.add('hide');
    }
    const filterList: any = this.filterState.basicFilterListValue;
    if (
      (filterList.DistressStatus || filterList.DistressStartDateWithin) &&
      distressInfo &&
      distressInfo.DistressStatus
    ) {
      document.querySelector(`#onlyDistressInfo${PMXPropertyId}`).classList.remove('hide');
      document.querySelector(`#onlyDistressInfo${PMXPropertyId} #distressStatus`).innerHTML =
        distressInfo.DistressStatus ? this.titleCasePipe.transform(distressInfo.DistressStatus) : '--';
    } else {
      document.querySelector(`#onlyDistressInfo${PMXPropertyId}`).classList.add('hide');
    }
  }

  getInfoWindowContent(propertyList: PartialProperty[]): string {
    let infoWindowContent: string = null;
    let matchedProperty: PartialProperty = propertyList[0];
    // Find the property that matches the matchedProperty.PMXPropertyId
    let activeProperty = this.propertyState.listPropertySetValue.find(
      (property) => property.PMXPropertyId === matchedProperty.PMXPropertyId
    );

    if (propertyList && propertyList.length == 1) {
      infoWindowContent = this.mapConfig.detailedInfoBox;
    } else {
      infoWindowContent = this.mapConfig.basicInfoBox;
    }

    infoWindowContent = infoWindowContent.replace(/#PMXPropertyId#/gi, activeProperty.PMXPropertyId);
    infoWindowContent = infoWindowContent.replace(
      /#favoriteFill#/gi,
      activeProperty.isFavorite ? '#ec5757' : '#D1D1D1'
    );
    infoWindowContent = infoWindowContent.replace(
      /#bedroomCount#/gi,
      this.toStringPipe.transform(activeProperty.BedroomsTotal) || '0'
    );
    infoWindowContent = infoWindowContent.replace(
      /#bathroomCount#/gi,
      this.toStringPipe.transform(activeProperty.BathroomsTotalInteger) || '0'
    );
    infoWindowContent = infoWindowContent.replace(
      /#livingArea#/gi,
      this.numberPipe.transform(activeProperty.LivingArea) || '0'
    );
    infoWindowContent = infoWindowContent.replace(
      /#lotSizeSquareFeet#/gi,
      this.numberPipe.transform(activeProperty.LotSizeSquareFeet) || '0'
    );
    infoWindowContent = infoWindowContent.replace(
      /#price#/gi,
      activeProperty.Lst_StandardStatus &&
        (activeProperty.Lst_StandardStatus === 'Active' ||
          activeProperty.Lst_StandardStatus === 'Active Under Contract' ||
          activeProperty.Lst_StandardStatus === 'Pending')
        ? activeProperty.Lst_ListPrice || activeProperty.CurrentAVMValue
          ? this.decimalPipe.transform(activeProperty.Lst_ListPrice || activeProperty.CurrentAVMValue)
          : '0'
        : activeProperty.CurrentAVMValue
          ? this.decimalPipe.transform(activeProperty.CurrentAVMValue)
          : '0'
    );
    infoWindowContent = infoWindowContent.replace(
      /#imageUrl#/gi,
      activeProperty.Lst_ListingId
        ? `${environment.listingImageBaseUrl}` +
        '?ListingId=' +
        `${activeProperty.Lst_ListingId || 'null'}` +
        '&imageName=1&height=219'
        : `${environment.listingImageBaseUrl}` + '?ListingId=0&imageName=1&height=219'
    );

    const fullAddress = activeProperty.Address || '';
    infoWindowContent = infoWindowContent.replace(/#fullAddress#/gi, fullAddress);

    if (propertyList.length > 1) {
      const groupStreetAddress: string = activeProperty.Address.substring(
        0,
        activeProperty.Address.indexOf('#') >= 0
          ? activeProperty.Address.indexOf('#')
          : activeProperty.Address.indexOf(',')
      ).trim();
      infoWindowContent = infoWindowContent.replace(/#address1#/gi, groupStreetAddress || '');
      infoWindowContent = infoWindowContent.replace(
        /#address2#/gi,
        `${activeProperty.City || ''} ${activeProperty.StateOrProvince || ''}, ${activeProperty.PostalCode || ''}`
      );
      infoWindowContent = infoWindowContent.replace(/#propertyType#/gi, activeProperty.PropertySubType || '--');
      infoWindowContent = infoWindowContent.replace(/#propertyCount#/gi, propertyList.length.toString() || '--');
    }

    return infoWindowContent;
  }
}
